import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Banknote, FileText, Clock, Users, ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Eyebrow, Reveal } from "@/components/Primitives";

const FEATURES = [
  [Banknote, "Direct deposit", "Pay your team on schedule, every schedule — weekly, bi-weekly or monthly."],
  [FileText, "Payroll tax filings", "Federal, state and local payroll tax calculations and filings, plus W-2s and 1099s."],
  [Clock, "Time & attendance", "Hours flow straight into payroll so there's less re-keying and fewer errors."],
  [Users, "Employee self-service", "Staff can view pay stubs and tax forms online without calling the office."],
];

export const Payroll = ({ onSelect }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <section id="payroll" className="px-3 sm:px-5" data-testid="payroll-section">
      <div ref={ref} className="relative bg-[var(--ink)] text-white rounded-[36px] overflow-hidden py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <Eyebrow light>02 — Payroll processing</Eyebrow>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.02]">
                Money in. Money out. <span className="font-serif-i text-[var(--mint)]">One partner.</span>
              </h2>
              <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl">
                The same team that processes your card payments can run your payroll too — fewer vendors, fewer logins
                and one point of contact when something needs fixing.
              </p>
            </Reveal>
            <div className="mt-12 grid sm:grid-cols-2 gap-x-8 gap-y-9">
              {FEATURES.map(([Icon, t, d], i) => (
                <Reveal key={t} delay={i * 0.08}>
                  <div className="border-t border-white/15 pt-5" data-testid={`payroll-feature-${i}`}>
                    <Icon className="h-5 w-5 text-[var(--mint)]" />
                    <h3 className="mt-4 font-display text-xl font-semibold">{t}</h3>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal className="mt-12">
              <button
                onClick={onSelect}
                data-testid="payroll-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-white text-[var(--ink)] px-7 py-4 font-semibold hover:bg-[var(--mint)] hover:text-white transition-colors"
              >
                Add payroll to my quote
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-6" delay={0.1}>
            <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] rounded-[28px] overflow-hidden [clip-path:polygon(0_6%,100%_0,100%_94%,0_100%)]">
              <motion.img
                src={IMAGES.payroll}
                alt="Business owner reviewing payroll reports with a colleague"
                style={{ y, scale: 1.2 }}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-[#2453FF]/20 mix-blend-multiply" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
